import { Injectable, signal, inject, computed, OnDestroy } from "@angular/core";
import { CreateDroneReportRequest, DroneOwnership, DroneReport, DroneReportCompleteEvent, DroneReportFailedEvent, DroneReportProgressEvent, DroneReportScope, DroneReportStatus, UpdateDroneReportRequest } from "../models/drone-report.model";
import { firstValueFrom, Observable, Subject, takeUntil } from "rxjs";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { WebSocketService } from "./websocket.service";
import { formatDateRange } from "src/app/shared/utils/date.utils";
import { MembershipRole } from "../models/team.model";
import { OrganizationContextService } from "./organization-context.service";
import { CustomClaimService } from "./custom-claim.service";

export const DRONE_REPORT_ROLES = [
    MembershipRole.Owner, 
    MembershipRole.ChiefSupervisor, 
    MembershipRole.PilotInCharge
]

export type DroneReportActionResult = { ok: true } | { ok: false; error: string };

/**
 * API Response wrapper type - matches the Hub's ResponseModel
 */
interface ApiResponse<T> {
  timestamp: number;
  status: number;
  data?: T;
  errors: string[];
}

/**
 * API List Response - matches the Hub's ListResponseModel
 */
interface ApiListResponse<T> extends ApiResponse<T[]> {
  paging: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

/**
 * Query options for listing drone reports
 */
interface DroneReportListOptions {
  page?: number;
  limit?: number;
  status?: DroneReportStatus;
  droneId?: string;
  droneSource?: DroneOwnership;
}

function isSuccess(response: ApiResponse<unknown>): boolean {
  return response.status >= 200 && response.status < 300;
}

@Injectable({
  providedIn: 'root'
})
export class DroneReportService implements OnDestroy {
    private http = inject(HttpClient);
    private customClaimService = inject(CustomClaimService);
    private organizationContext = inject(OrganizationContextService);
    private webSocketService = inject(WebSocketService);
    private apiUrl = environment.apiUrl;
    private destroy$ = new Subject<void>();
    private isSubscribed = false;

    // ============================================
    // STATE
    // ============================================

    private reportsSignal = signal<DroneReport[]>([]);
    private loadingSignal = signal<boolean>(false);
    private errorSignal = signal<string | null>(null);
    private totalCountSignal = signal<number>(0);
    private currentPageSignal = signal<number>(1);
    private pageSizeSignal = signal<number>(10);

    readonly reports = computed(() => this.reportsSignal());
    readonly error = computed(() => this.errorSignal());

    // Computed stats
    readonly generatingReports = computed(() =>
        this.reportsSignal().filter(r => r.status === 'generating')
    );

    readonly readyReports = computed(() =>
        this.reportsSignal().filter(r => r.status === 'ready')
    );

    readonly failedReports = computed(() =>
        this.reportsSignal().filter(r => r.status === 'failed')
    );

    // ============================================
    // LIFECYCLE
    // ============================================

    constructor() {
        this.webSocketService.connectionState$.pipe(
            takeUntil(this.destroy$)
        ).subscribe(state => {
            if (state === 'authenticated' && !this.isSubscribed) {
                this.setupWebSocketSubscriptions();
            } else if (state === 'disconnected') {
                this.isSubscribed = false;
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ============================================
    // WEBSOCKET SUBSCRIPTIONS
    // ============================================

    /**
     * Set up WebSocket subscriptions for real-time Drone report updates.
     * Listens for progress, complete, and failed events.
     * Called when WebSocket connection is authenticated.
     */
    private setupWebSocketSubscriptions(): void {
        if (this.isSubscribed) return;
        this.isSubscribed = true;

        console.log('[DroneReportService] Setting up WebSocket subscriptions for Drone report events');

        // Subscribe to progress events
        this.webSocketService.on<DroneReportProgressEvent>('drone-report:progress')
            .pipe(takeUntil(this.destroy$))
            .subscribe(event => {
                console.log('[DroneReportService] Received drone-report:progress:', event);
                this.handleProgressEvent(event);
            });

        // Subscribe to complete events
        this.webSocketService.on<DroneReportCompleteEvent>('drone-report:complete')
            .pipe(takeUntil(this.destroy$))
            .subscribe(event => {
                console.log('[DroneReportService] Received drone-report:complete:', event);
                this.handleCompleteEvent(event);
            });

        // Subscribe to failed events
        this.webSocketService.on<DroneReportFailedEvent>('drone-report:failed')
            .pipe(takeUntil(this.destroy$))
            .subscribe(event => {
                console.log('[DroneReportService] Received drone-report:failed:', event);
                this.handleFailedEvent(event);
            });

        console.log('[DroneReportService] WebSocket subscriptions established');
    }

    /**
     * Handle progress event from WebSocket
     */
    private handleProgressEvent(event: DroneReportProgressEvent): void {
        this.reportsSignal.update(reports => {
            return reports.map(report => {
                if (report.id === event.reportId) {
                    return {
                        ...report,
                        status: event.status,
                        generationProgress: event.progress
                    };
                }
                return report;
            });
        });
    }

    /**
     * Handle complete event from WebSocket
     */
    private handleCompleteEvent(event: DroneReportCompleteEvent): void {
        this.reportsSignal.update(reports => {
            return reports.map(report => {
                if (report.id === event.reportId) {
                    return {
                        ...report,
                        status: event.status,
                        reportUrl: event.url,
                        recordCount: event.recordCount,
                        fileSize: event.fileSize,
                        generationProgress: 100
                    };
                }
                return report;
            });
        });
    }

    /**
     * Handle failed event from WebSocket
     */
    private handleFailedEvent(event: DroneReportFailedEvent): void {
        this.reportsSignal.update(reports => {
            return reports.map(report => {
                if (report.id === event.reportId) {
                    return {
                        ...report,
                        status: event.status,
                        errorMessage: event.error,
                        generationProgress: 0
                    };
                }
                return report;
            });
        });
    }

    // ============================================
    // LIST
    // ============================================

    /**
     * List drone reports for an organization with pagination
     *
     * @param orgId Organization ID
     * @param options Optional query parameters (page, limit, status, droneId + droneSource)
     * @returns Observable with paginated report list
     */
    listReports(
        orgId: string,
        options?: DroneReportListOptions
    ): Observable<ApiListResponse<DroneReport>> {
        let params = new HttpParams();

        // The Hub requires droneId and droneSource together
        if (options?.droneId && options?.droneSource) {
            params = params.set('droneId', options.droneId);
            params = params.set('droneSource', options.droneSource);
        }

        if (options?.page) {
            params = params.set('page', options.page.toString());
        }
        if (options?.limit) {
            params = params.set('limit', options.limit.toString());
        }
        if (options?.status) {
            params = params.set('status', options.status);
        }

        return this.http.get<ApiListResponse<DroneReport>>(
            `${this.apiUrl}/organizations/${orgId}/drone-reports`,
            { params }
        );
    }

    /**
     * Load reports into signal state
     *
     * @param orgId Organization ID
     * @param options Optional query parameters
     */
    async loadReports(
        orgId: string,
        options?: DroneReportListOptions
    ): Promise<void> {
        this.loadingSignal.set(true);
        this.errorSignal.set(null);
        // Clear the previous drone's reports so they don't flash while this list loads
        this.reportsSignal.set([]);

        try {
            const response = await firstValueFrom(this.listReports(orgId, options));

            if (isSuccess(response) && response.data) {
                this.reportsSignal.set(response.data.map(r => this.mapApiToReport(r)));
                this.totalCountSignal.set(response.paging?.totalItems ?? 0);
                this.currentPageSignal.set(response.paging?.page ?? 1);
                this.pageSizeSignal.set(response.paging?.pageSize ?? 10);
            } else {
                throw new Error(response.errors?.[0] || 'Failed to load reports');
            }
        } catch (error: any) {
            console.error('Error loading Drone reports:', error);
            this.errorSignal.set(this.getErrorMessage(error, 'Failed to load reports'));
        } finally {
            this.loadingSignal.set(false);
        }
    }

    // ============================================
    // GET
    // ============================================

    /**
     * Get a single Drone report by ID
     *
     * @param orgId Organization ID
     * @param reportId Report ID
     * @returns Observable with single report
     */
    getReport(orgId: string, reportId: string): Observable<ApiResponse<DroneReport>> {
        return this.http.get<ApiResponse<DroneReport>>(
            `${this.apiUrl}/organizations/${orgId}/drone-reports/${reportId}`
        );
    }

    /**
     * Fetch and return a single report
     *
     * @param orgId Organization ID
     * @param reportId Report ID
     * @returns Promise with report or null
     */
    async fetchReport(orgId: string, reportId: string): Promise<DroneReport | null> {
        try {
            const response = await firstValueFrom(this.getReport(orgId, reportId));

            if (isSuccess(response) && response.data) {
                return this.mapApiToReport(response.data);
            }
            return null;
        } catch (error: any) {
            console.error('Error fetching Drone report:', error);
            return null;
        }
    }

    // ============================================
    // CREATE
    // ============================================

    /**
     * Create a new drone report in draft status
     *
     * @param orgId Organization ID
     * @param data Report creation data
     * @returns Observable with created report
     */
    createReport(
        orgId: string,
        data: CreateDroneReportRequest
    ): Observable<ApiResponse<DroneReport>> {
        return this.http.post<ApiResponse<DroneReport>>(
            `${this.apiUrl}/organizations/${orgId}/drone-reports`,
            data
        );
    }

    /**
     * Create report and add to signal state
     *
     * @param orgId Organization ID
     * @param data Report creation data
     * @returns Promise with created report or null
     */
    async createAndAddReport(
        orgId: string,
        data: CreateDroneReportRequest
    ): Promise<DroneReport | null> {
        this.loadingSignal.set(true);
        this.errorSignal.set(null);

        try {
            const response = await firstValueFrom(this.createReport(orgId, data));

            if (isSuccess(response) && response.data) {
                const newReport = this.mapApiToReport(response.data);
                this.reportsSignal.update(reports => [newReport, ...reports]);
                this.totalCountSignal.update(count => count + 1);
                return newReport;
            } else {
                throw new Error(response.errors?.[0] || 'Failed to create report');
            }
        } catch (error: any) {
            console.error('Error creating drone report:', error);
            this.errorSignal.set(this.getErrorMessage(error, 'Failed to create report'));
            return null;
        } finally {
            this.loadingSignal.set(false);
        }
    }

    // ============================================
    // GENERATE
    // ============================================

    /**
     * Generate PDF for a Drone report (queues background job)
     *
     * @param orgId Organization ID
     * @param reportId Report ID
     * @returns Observable with response
     */
    generatePdf(orgId: string, reportId: string): Observable<ApiResponse<{ jobId: string }>> {
        return this.http.post<ApiResponse<{ jobId: string }>>(
            `${this.apiUrl}/organizations/${orgId}/drone-reports/${reportId}/generate`,
            {}
        );
    }

    /**
     * Trigger PDF generation and update signal state
     *
     * @param orgId Organization ID
     * @param reportId Report ID
     * @returns Promise with success boolean
     */
    async triggerPdfGeneration(orgId: string, reportId: string): Promise<boolean> {
        try {
            const response = await firstValueFrom(this.generatePdf(orgId, reportId));

            if (isSuccess(response)) {
                // Update local state to show generating status
                this.reportsSignal.update(reports => {
                    return reports.map(r => {
                        if (r.id === reportId) {
                            return {
                                ...r,
                                status: 'generating' as DroneReportStatus,
                                generationProgress: 0
                            };
                        }
                        return r;
                    });
                });
                return true;
            } else {
                throw new Error(response.errors?.[0] || 'Failed to start generation');
            }
        } catch (error: any) {
            console.error('Error generating Drone report PDF:', error);
            this.errorSignal.set(this.getErrorMessage(error, 'Failed to start generation'));
            return false;
        }
    }

    // ============================================
    // RETRY
    // ============================================

    /**
     * Retry a failed report generation
     *
     * @param orgId Organization ID
     * @param reportId Report ID
     * @returns Observable with response
     */
    retryReport(orgId: string, reportId: string): Observable<ApiResponse<{ jobId: string }>> {
        return this.http.post<ApiResponse<{ jobId: string }>>(
            `${this.apiUrl}/organizations/${orgId}/drone-reports/${reportId}/retry`,
            {}
        );
    }

    /**
     * Retry failed report and update signal state
     *
     * @param orgId Organization ID
     * @param reportId Report ID
     * @returns Promise with success boolean
     */
    async retryFailedReport(orgId: string, reportId: string): Promise<boolean> {
        try {
            const response = await firstValueFrom(this.retryReport(orgId, reportId));

            if (isSuccess(response)) {
                // Update local state to show generating status
                this.reportsSignal.update(reports => {
                    return reports.map(r => {
                        if (r.id === reportId) {
                            return {
                                ...r,
                                status: 'generating' as DroneReportStatus,
                                generationProgress: 0,
                                errorMessage: null
                            };
                        }
                        return r;
                    });
                });
                return true;
            } else {
                throw new Error(response.errors?.[0] || 'Failed to retry generation');
            }
        } catch (error: any) {
            console.error('Error retrying Drone report:', error);
            this.errorSignal.set(this.getErrorMessage(error, 'Failed to retry generation'));
            return false;
        }
    }

    // ============================================
    // DELETE
    // ============================================

    /**
     * Delete a drone report (soft delete)
     *
     * @param orgId Organization ID
     * @param reportId Report ID
     * @returns Observable with void response
     */
    deleteReport(orgId: string, reportId: string): Observable<ApiResponse<void>> {
        return this.http.delete<ApiResponse<void>>(
            `${this.apiUrl}/organizations/${orgId}/drone-reports/${reportId}`
        );
    }

    /**
     * Delete report and remove from signal state
     *
     * @param orgId Organization ID
     * @param reportId Report ID
     * @returns Promise with success boolean
     */
    async deleteAndRemoveReport(orgId: string, reportId: string): Promise<boolean> {
        try {
            // DELETE returns 204 No Content on success, so response body may be null
            const response = await firstValueFrom(this.deleteReport(orgId, reportId));

            // Treat null response (204) or response.success === true as success
            if (response === null || isSuccess(response)) {
                this.reportsSignal.update(reports => reports.filter(r => r.id !== reportId));
                this.totalCountSignal.update(count => Math.max(0, count - 1));
                return true;
            } else {
                throw new Error(response.errors?.[0] || 'Failed to delete report');
            }
        } catch (error: any) {
            console.error('Error deleting Drone report:', error);
            this.errorSignal.set(this.getErrorMessage(error, 'Failed to delete report'));
            return false;
        }
    }

    // ============================================
    // DISPLAY HELPERS
    // ============================================

    /**
     * Get Bootstrap icon class for status
     */
    getStatusIcon(status: DroneReportStatus): string {
        switch (status) {
            case 'draft': return 'bi-file-earmark';
            case 'generating': return 'bi-arrow-repeat';
            case 'ready': return 'bi-check-circle';
            case 'submitted': return 'bi-send-check';
            case 'failed': return 'bi-exclamation-triangle';
            default: return 'bi-file-earmark';
        }
    }

    /**
     * Get human-readable status text
     */
    getStatusText(status: DroneReportStatus): string {
        switch (status) {
            case 'draft': return 'Draft';
            case 'generating': return 'Generating...';
            case 'ready': return 'Ready';
            case 'submitted': return 'Submitted';
            case 'failed': return 'Failed';
            default: return status;
        }
    }

    /**
     * Format date range for display, e.g. "Oct 1, 2026 - Oct 15, 2026"
     */
    formatDateRange(startDate: string, endDate: string, options: { format?: Intl.DateTimeFormatOptions; separator?: string } = {}, range: boolean = true): string | null {
        return formatDateRange(startDate, endDate, options, range);
    }

    /**
     * A report's dates for display: a range for full history, a single date
     * otherwise, or '' when the report has none
     */
    formatReportDates(report: Pick<DroneReport, 'startDate' | 'endDate' | 'scope'>): string {
        return formatDateRange(report.startDate, report.endDate, {}, report.scope === 'full_history') ?? '';
    }

    /**
     * Human-readable report scope
     */
    getScopeLabel(scope: DroneReportScope): string {
        return scope === 'full_history' ? 'Full History' : 'Single Incident';
    }

    /**
     * Clear error state
     */
    clearError(): void {
        this.errorSignal.set(null);
    }

    // ============================================
    // PRIVATE HELPERS
    // ============================================

    /**
     * Prefer the Hub's error message from a non-2xx response (HttpErrorResponse
     * carries the ResponseModel body in `error`), then the thrown message.
     */
    private getErrorMessage(error: any, fallback: string): string {
        return error?.error?.errors?.[0] || error?.message || fallback;
    }

    /**
     * Map API response to DroneReport interface
     * Handles both snake_case and camelCase field names
     */
    private mapApiToReport(data: any): DroneReport {
        return {
            id: data.id,
            organizationId: data.organizationId ?? data.organization_id,
            droneId: data.droneId ?? data.drone_id,
            droneSource: data.droneSource ?? data.drone_source,
            name: data.name,
            scope: data.scope ?? data.scope,
            sprayRecordId: data.sprayRecordId ?? data.spray_record_id,
            status: data.status ?? 'draft',
            startDate: data.startDate ?? data.start_date,
            endDate: data.endDate ?? data.end_date,
            generationProgress: data.generationProgress ?? data.generation_progress ?? 0,
            recordCount: data.recordCount ?? data.record_count,
            fileSize: data.fileSize ?? data.file_size,
            reportUrl: data.reportUrl ?? data.report_url,
            downloadUrl: data.downloadUrl ?? data.download_url,
            submittedAt: data.submittedAt ?? data.submitted_at,
            submittedTo: data.submittedTo ?? data.submitted_to,
            errorMessage: data.errorMessage ?? data.error_message,
            createdAt: data.createdAt ?? data.created_at,
            updatedAt: data.updatedAt ?? data.updated_at,
        };
    }

    canManageReports(): boolean {
        const orgId = this.organizationContext.currentOrganizationId();
        return !!orgId && this.customClaimService.hasAnyOrgRole(DRONE_REPORT_ROLES, orgId);
    }

    // ============================================
    // ACTION METHODS
    // ============================================

    async startReport(request: CreateDroneReportRequest): Promise<DroneReport | null> {
        const orgId = this.organizationContext.currentOrganizationId();
        if (!orgId) return null;

        const report = await this.createAndAddReport(orgId, request);
        if (!report) return null;

        const reportStarted = await this.triggerPdfGeneration(orgId, report.id);
        return reportStarted ? report : null;
    }

    async getDownloadUrl(report: DroneReport): Promise<string | null> {
        if (report.status !== 'ready' && report.status !== 'submitted') return null;

        const orgId = this.organizationContext.currentOrganizationId();
        if (!orgId) return null;

        // Fetch fresh report data to get a valid pre-signed URL
        const freshReport = await this.fetchReport(orgId, report.id);

        // Use downloadUrl (pre-signed URL) for download
        if (!freshReport?.downloadUrl) {
            this.errorSignal.set('The download link could not be created. Please try again.');
            return null;        
        }
        return freshReport.downloadUrl;
    }

    async removeReport(report: DroneReport): Promise<DroneReportActionResult> {
        // Cannot delete while generating
        if (report.status === 'generating') {
            return { ok: false, error: 'Cannot delete a report while it is generating' };
        }

        const orgId = this.organizationContext.currentOrganizationId();
        if (!orgId) {
            return { ok: false, error: 'No organization selected' };
        }

        const success = await this.deleteAndRemoveReport(orgId, report.id);
        if (success) {
            return { ok: true };
        }

        const error = this.error() ?? 'Failed to delete report';
        this.clearError();
        return { ok: false, error };
    }

}
